<script setup>
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue';
import { useFlowieStore } from '../../../stores/flowieStore.js';
import { useNotificationStore } from '@/stores/NotificationStore.js';
import Flowie from './avatars/flowie.vue';
import AiAgentAiOrchestratorAvatar from '../../agents/ai-orchestrator/avatar.vue';

const store = useFlowieStore();
const notificationStore = useNotificationStore();

const chatInput = ref('');
const chatInputField = ref(null);
const chatContainer = ref(null);
const showConversations = ref(false);
const showModeDropdown = ref(false);
const modeDropdown = ref(null);
const funMessageIndex = ref(0);
const funMessageInterval = ref(null);
const progressWidth = ref(0);
const progressInterval = ref(null);

const isRecording = ref(false);
const isTranscribing = ref(false);
let mediaRecorder = null;
let audioChunks = [];

const specialistColors = {
    stella: 'bg-aiAgentWebDesigner',
    maya: 'bg-aiAgentContentCreator',
    oscar: 'bg-aiAgentLeadFinder',
    alex: 'bg-aiAgentNewsletterSpecialist',
    tom: 'bg-aiAgentRelationshipManager',
    nina: 'bg-aiAgentSalesAssistant',
    susan: 'bg-aiAgentDataAnalyst',
    leo: 'bg-aiAgentAutomationExpert',
};

const specialistLinks = {
    stella: '/page/create/landingpage?title=Home&ai_page=1',
    maya: '/bo/ai-content-creator/create',
    oscar: '/bo/hugo',
    alex: '/bo/alex',
    tom: '/bo/tom',
    nina: '/bo/nina',
    susan: '/bo/susan',
    leo: '/bo/leo',
};

const getTaskLink = (task) => task.link || specialistLinks[task.specialist];

const isSpecialistHired = (task) => {
    const hiredSpecialists = window.myflow?.company?.hired_specialists;

    // If the shared company context is unavailable, leave the link enabled and
    // let the existing backend middleware remain the authoritative guard.
    return (
        !Array.isArray(hiredSpecialists) ||
        hiredSpecialists.includes(task.specialist)
    );
};

const funMessages = [
    {
        icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z',
        text: () => _mft('dashboard:flowie.generating.thinking'),
    },
    {
        icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25',
        text: () => _mft('dashboard:flowie.generating.analyzing'),
    },
    {
        icon: 'M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z',
        text: () => _mft('dashboard:flowie.generating.planning'),
    },
    {
        icon: 'M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z',
        text: () => _mft('dashboard:flowie.generating.crafting'),
    },
    {
        icon: 'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z',
        text: () => _mft('dashboard:flowie.generating.finishing'),
    },
];

const starterPrompts = [
    {
        key: 'newCustomers',
        messageKey: 'dashboard:flowie.starterPrompts.newCustomers',
        bubbleClass: 'bg-aiAgentLeadFinder/20 border-aiAgentLeadFinder/40',
    },
    {
        key: 'newWebsite',
        messageKey: 'dashboard:flowie.starterPrompts.newWebsite',
        bubbleClass: 'bg-aiAgentWebDesigner/20 border-aiAgentWebDesigner/40',
    },
    {
        key: 'sendNewsletter',
        messageKey: 'dashboard:flowie.starterPrompts.sendNewsletter',
        bubbleClass:
            'bg-aiAgentNewsletterSpecialist/20 border-aiAgentNewsletterSpecialist/40',
    },
    {
        key: 'createContent',
        messageKey: 'dashboard:flowie.starterPrompts.createContent',
        bubbleClass:
            'bg-aiAgentContentCreator/20 border-aiAgentContentCreator/40',
    },
    {
        key: 'automaticEmails',
        messageKey: 'dashboard:flowie.starterPrompts.automaticEmails',
        bubbleClass:
            'bg-aiAgentAutomationExpert/20 border-aiAgentAutomationExpert/40',
    },
    {
        key: 'customerNeeds',
        messageKey: 'dashboard:flowie.starterPrompts.customerNeeds',
        bubbleClass:
            'bg-aiAgentRelationshipManager/20 border-aiAgentRelationshipManager/40',
    },
    // {
    //     key: 'winningQuote',
    //     messageKey: 'dashboard:flowie.starterPrompts.winningQuote',
    //     bubbleClass:
    //         'bg-aiAgentSalesAssistant/20 border-aiAgentSalesAssistant/40',
    // },
    {
        key: 'timeSavingCrm',
        messageKey: 'dashboard:flowie.starterPrompts.timeSavingCrm',
        bubbleClass: 'bg-aiAgentDataAnalyst/20 border-aiAgentDataAnalyst/40',
    },
];

const props = defineProps({
    focusOnLoad: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const currentFunMessage = computed(
    () => funMessages[funMessageIndex.value % funMessages.length]
);

const messages = computed(() => store.activeConversation?.messages || []);
const tasks = computed(() => store.activeConversation?.tasks || []);
const latestActionPlanIndex = computed(() => {
    for (let index = messages.value.length - 1; index >= 0; index -= 1) {
        if (messages.value[index]?.type === 'action_plan') {
            return index;
        }
    }

    return -1;
});
const isNewConversation = computed(() => messages.value.length === 0);
const conversationTitle = computed(() => store.activeConversation?.title || '');
const inputPlaceholder = computed(() =>
    store.pendingMode === 'project'
        ? _mft('dashboard:flowie.projectInputPlaceholder')
        : _mft('dashboard:flowie.inputPlaceholder')
);
const isProjectModeEnabled = computed({
    get: () => store.pendingMode === 'project',
    set: (value) => store.setPendingMode(value ? 'project' : null),
});
const currentModeLabel = computed(() =>
    isProjectModeEnabled.value
        ? _mft('dashboard:flowie.projectMode')
        : _mft('dashboard:flowie.chatMode')
);
const modeOptions = computed(() => [
    {
        mode: null,
        label: _mft('dashboard:flowie.chatMode'),
        description: _mft('dashboard:flowie.chatModeDescription'),
        selected: !isProjectModeEnabled.value,
    },
    {
        mode: 'project',
        label: _mft('dashboard:flowie.projectMode'),
        description: _mft('dashboard:flowie.projectModeDescription'),
        selected: isProjectModeEnabled.value,
    },
]);

const startFunMessages = () => {
    funMessageIndex.value = Math.floor(Math.random() * funMessages.length);
    progressWidth.value = 0;

    funMessageInterval.value = setInterval(() => {
        funMessageIndex.value =
            (funMessageIndex.value + 1) % funMessages.length;
    }, 3500);

    let elapsed = 0;
    progressInterval.value = setInterval(() => {
        elapsed += 200;
        progressWidth.value = Math.min(90, 30 * Math.log10(elapsed / 500 + 1));
    }, 200);
};

const stopFunMessages = () => {
    if (funMessageInterval.value) {
        clearInterval(funMessageInterval.value);
        funMessageInterval.value = null;
    }
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
        progressInterval.value = null;
    }
    progressWidth.value = 100;
};

watch(
    () => store.isGenerating,
    (val) => {
        if (val) startFunMessages();
        else stopFunMessages();
    }
);

const scrollToBottom = () => {
    nextTick(() => {
        const scroll = () => {
            if (chatContainer.value) {
                chatContainer.value.scrollTop =
                    chatContainer.value.scrollHeight;
            }
        };

        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(scroll);
            return;
        }

        scroll();
    });
};

watch(() => messages.value.length, scrollToBottom, { flush: 'post' });
watch(isNewConversation, (value) => {
    if (!value) {
        showModeDropdown.value = false;
    }
});
watch(() => store.activeConversation?.id, scrollToBottom, { flush: 'post' });
watch(() => store.isGenerating, scrollToBottom, { flush: 'post' });
watch(chatInput, () => {
    nextTick(() => {
        resizeChatInput();
    });
});

const resizeChatInput = () => {
    if (!chatInputField.value) {
        return;
    }

    chatInputField.value.style.height = 'auto';

    const lineHeight =
        parseFloat(window.getComputedStyle(chatInputField.value).lineHeight) ||
        20;
    const maxHeight = lineHeight * 3 + 16;

    chatInputField.value.style.height = `${Math.min(
        chatInputField.value.scrollHeight,
        maxHeight
    )}px`;
};

const sendMessage = (text) => {
    const message = text || chatInput.value.trim();
    if (!message || store.isGenerating) return;
    chatInput.value = '';
    nextTick(() => {
        resizeChatInput();
    });
    store.sendMessage(message);
};

const openConversation = (id) => {
    store.loadConversation(id);
    showConversations.value = false;
};

const toggleModeDropdown = () => {
    showModeDropdown.value = !showModeDropdown.value;
};

const selectMode = (mode) => {
    store.setPendingMode(mode);
    showModeDropdown.value = false;
};

const handleDocumentClick = (e) => {
    if (!showModeDropdown.value || modeDropdown.value?.contains(e.target)) {
        return;
    }

    showModeDropdown.value = false;
};

const handleKeydown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
    }
};

const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('dashboard:flowie.voiceTranscribeError'),
            dismissAfter: 4000,
        });
        return;
    }
    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
        });
        const mimeType = MediaRecorder.isTypeSupported('audio/webm')
            ? 'audio/webm'
            : 'audio/mp4';
        mediaRecorder = new MediaRecorder(stream, { mimeType });
        audioChunks = [];

        mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) audioChunks.push(e.data);
        };

        mediaRecorder.onstop = async () => {
            stream.getTracks().forEach((t) => t.stop());
            isTranscribing.value = true;

            try {
                const blob = new Blob(audioChunks, {
                    type: mediaRecorder.mimeType,
                });
                const formData = new FormData();
                formData.append(
                    'audio',
                    blob,
                    'recording.' + (mimeType === 'audio/webm' ? 'webm' : 'mp4')
                );

                const response = await fetch('/bo/voice-transcribe', {
                    method: 'POST',
                    headers: {
                        'X-CSRF-TOKEN': document.querySelector(
                            'meta[name="csrf-token"]'
                        )?.content,
                    },
                    body: formData,
                });

                const data = await response.json();
                if (data.text) {
                    chatInput.value = data.text;
                }
            } catch (err) {
                console.error('Voice transcription failed:', err);
            } finally {
                isTranscribing.value = false;
            }
        };

        mediaRecorder.start();
        isRecording.value = true;
    } catch (err) {
        console.error('Microphone access denied:', err);
    }
};

const stopRecording = () => {
    isRecording.value = false;
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
    }
};

const renderInlineMarkdown = (text) =>
    text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>');

const renderMarkdown = (text) => {
    if (!text) return '';

    const blocks = [];
    let paragraphLines = [];
    let currentList = null;

    const flushParagraph = () => {
        if (!paragraphLines.length) return;

        blocks.push(
            `<p>${paragraphLines.map(renderInlineMarkdown).join('<br>')}</p>`
        );
        paragraphLines = [];
    };

    const flushList = () => {
        if (!currentList) return;

        const tag = currentList.type;
        const listClass =
            tag === 'ol' ? 'list-decimal pl-5 my-2' : 'list-disc pl-5 my-2';
        const items = currentList.items
            .map((item) => `<li class="mb-2">${renderInlineMarkdown(item)}</li>`)
            .join('');

        blocks.push(`<${tag} class="${listClass}">${items}</${tag}>`);
        currentList = null;
    };

    const addListItem = (type, content) => {
        flushParagraph();

        if (currentList?.type !== type) {
            flushList();
            currentList = {
                type,
                items: [],
            };
        }

        currentList.items.push(content);
    };

    text.split(/\r?\n/).forEach((line) => {
        const numberedItem = line.match(/^\s*\d+\.\s+(.+)$/);
        const bulletItem = line.match(/^\s*[-•]\s+(.+)$/);

        if (numberedItem) {
            addListItem('ol', numberedItem[1]);
            return;
        }

        if (bulletItem) {
            addListItem('ul', bulletItem[1]);
            return;
        }

        if (line.trim() === '') {
            flushParagraph();
            return;
        }

        flushList();
        paragraphLines.push(line);
    });

    flushList();
    flushParagraph();

    return blocks.join('');
};

onMounted(() => {
    document.addEventListener('click', handleDocumentClick);
    resizeChatInput();
    if (props.focusOnLoad) {
        chatInputField.value.focus();
    }
    scrollToBottom();
    store.loadConversations().then(async () => {
        await store.restoreConversationOnOpen();
        scrollToBottom();
    });
});

onUnmounted(() => {
    document.removeEventListener('click', handleDocumentClick);
    stopFunMessages();
    store.stopPolling();
});
</script>

<template>
    <card-basic :padless="true" overflow="visible">
        <!-- Header -->
        <div
            class="flex flex-col gap-3 p-4 border-b border-gray-100 sm:flex-row sm:items-start sm:justify-between"
        >
            <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                    <h2 class="text-lg font-semibold text-gray-800">Flowie</h2>
                    <div
                        v-if="isNewConversation"
                        ref="modeDropdown"
                        class="relative"
                    >
                        <button
                            type="button"
                            class="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-600 shadow-sm transition-colors duration-200 hover:border-wlPrimary/30 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-wlPrimary/25"
                            :aria-expanded="showModeDropdown"
                            aria-haspopup="true"
                            @click.stop="toggleModeDropdown"
                        >
                            <span>{{ currentModeLabel }}</span>
                            <svg
                                class="h-3.5 w-3.5 transition-transform duration-200"
                                :class="{ 'rotate-180': showModeDropdown }"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="m6 9 6 6 6-6" />
                            </svg>
                        </button>

                        <transition
                            enter-active-class="transition duration-150 ease-out"
                            enter-from-class="scale-95 opacity-0"
                            enter-to-class="scale-100 opacity-100"
                            leave-active-class="transition duration-100 ease-in"
                            leave-from-class="scale-100 opacity-100"
                            leave-to-class="scale-95 opacity-0"
                        >
                            <div
                                v-if="showModeDropdown"
                                class="absolute left-0 z-20 mt-2 w-72 origin-top-left rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
                            >
                                <button
                                    v-for="modeOption in modeOptions"
                                    :key="modeOption.label"
                                    type="button"
                                    class="flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                                    :class="{
                                        'bg-wlPrimary/5': modeOption.selected,
                                    }"
                                    @click="selectMode(modeOption.mode)"
                                >
                                    <span class="min-w-0 flex-1">
                                        <span
                                            class="block text-sm font-semibold text-gray-800"
                                        >
                                            {{ modeOption.label }}
                                        </span>
                                        <span
                                            class="mt-0.5 block text-xs leading-4 text-gray-500"
                                        >
                                            {{ modeOption.description }}
                                        </span>
                                    </span>
                                    <svg
                                        v-if="modeOption.selected"
                                        class="mt-0.5 h-4 w-4 flex-shrink-0 text-wlPrimary"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <path d="m5 12 5 5L20 7" />
                                    </svg>
                                </button>
                            </div>
                        </transition>
                    </div>
                </div>
                <p class="text-xs text-gray-500 mb-0">
                    {{ conversationTitle }}
                </p>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-2">
                <button-base
                    v-if="store.conversations.conversations.length"
                    @click="showConversations = !showConversations"
                    style-type="default"
                    size="sm"
                    class="gap-2"
                >
                    <span>
                        {{ _mft('dashboard:flowie.projects.conversations') }}
                        ({{ store.conversations.conversations.length }})
                    </span>
                </button-base>
                <button-secondary
                    v-if="messages.length > 0"
                    @click="store.newConversation()"
                    size="sm"
                >
                    {{ _mft('dashboard:flowie.newChat') }}
                </button-secondary>
            </div>
        </div>

        <!-- Conversations list -->
        <div
            v-if="showConversations && store.conversations.conversations.length"
            class="px-3 border-b border-gray-100 bg-gray-50"
        >
            <div class="space-y-1 max-h-48 overflow-y-auto px-1 py-3">
                <button-default
                    v-for="chat in store.conversations.conversations"
                    :key="chat.id"
                    @click="openConversation(chat.id)"
                    size="md"
                    class="w-full !justify-start !text-left truncate"
                    :class="{
                        '!ring-2 !ring-wlPrimary':
                            store.activeConversation?.id === chat.id,
                    }"
                >
                    {{
                        chat.title || _mft('dashboard:flowie.projects.untitled')
                    }}
                </button-default>
            </div>
        </div>

        <!-- Messages -->
        <div
            ref="chatContainer"
            class="min-h-[300px] max-h-[500px] overflow-y-auto p-4"
        >
            <div class="flex min-h-full flex-col justify-end">
                <!-- Welcome state — two entry points -->
                <div
                    v-if="messages.length === 0 && !store.isGenerating"
                    class="mx-auto flex w-full max-w-3xl flex-col items-center justify-center py-0 text-center lg:py-8"
                >
                    <div
                        class="flowie-starter-avatar mb-4 flex h-10 w-10 lg:h-20 lg:w-20 items-center justify-center rounded-full"
                    >
                        <ai-agent-ai-orchestrator-avatar
                            class="w-10 h-10 lg:w-20 lg:h-20 text-wlPrimary"
                        />
                    </div>
                    <p class="mb-3 text-base font-semibold text-gray-900">
                        {{ _mft('dashboard:flowie.welcomeMessage') }}
                    </p>

                    <div class="mb-5 w-full">
                        <div
                            class="mx-auto flex w-full max-w-3xl flex-wrap items-start justify-center gap-2.5"
                        >
                            <button
                                v-for="starterPrompt in starterPrompts"
                                :key="starterPrompt.key"
                                type="button"
                                class="flowie-starter-bubble group inline-flex w-fit max-w-[92%] self-start items-center gap-2 border px-3.5 py-2.5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-wlPrimary/25 disabled:cursor-not-allowed disabled:opacity-60 lg:max-w-[78%]"
                                :class="starterPrompt.bubbleClass"
                                :disabled="store.isGenerating"
                                @click="
                                    sendMessage(_mft(starterPrompt.messageKey))
                                "
                            >
                                <span
                                    class="min-w-0 flex-1 text-xs lg:text-sm leading-5 text-gray-800"
                                >
                                    {{ _mft(starterPrompt.messageKey) }}
                                </span>
                                <span
                                    class="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-white/70 px-2 py-1 text-[11px] font-semibold text-gray-500 shadow-sm transition-colors duration-200 group-hover:bg-white group-hover:text-wlPrimary"
                                >
                                    <span>{{
                                        _mft(
                                            'dashboard:flowie.starterPrompts.send'
                                        )
                                    }}</span>
                                    <svg
                                        class="h-3.5 w-3.5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <line x1="22" y1="2" x2="11" y2="13" />
                                        <polygon
                                            points="22 2 15 22 11 13 2 9 22 2"
                                        />
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </div>

                    <p class="mb-5 max-w-xl text-sm leading-5 text-gray-500">
                        {{ _mft('dashboard:flowie.modeDescription') }}
                    </p>
                </div>

                <!-- Chat messages -->
                <template v-for="(msg, i) in messages" :key="i">
                    <div
                        class="flex gap-3 mb-3"
                        :class="{ 'justify-end': msg.role === 'user' }"
                    >
                        <!-- Flowie avatar -->
                        <div
                            v-if="msg.role === 'assistant'"
                            class="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-wlPrimary/10"
                        >
                            <ai-agent-ai-orchestrator-avatar class="w-6 h-6" />
                        </div>

                        <div
                            class="px-4 py-3 rounded-lg max-w-[80%]"
                            :class="
                                msg.role === 'user'
                                    ? 'bg-wlPrimary text-white'
                                    : 'bg-gray-50 text-gray-700'
                            "
                        >
                            <!-- Text content -->
                            <div
                                v-if="msg.role === 'assistant'"
                                class="text-sm"
                                v-html="renderMarkdown(msg.content)"
                            ></div>
                            <p v-else class="text-sm whitespace-pre-wrap !mb-0">
                                {{ msg.content }}
                            </p>

                            <utility-ai-usage-cta
                                v-if="msg.budgetExceeded"
                                class="mt-3"
                            />

                            <!-- Project proposal (inline) -->
                            <div
                                v-if="
                                    msg.type === 'project_proposal' &&
                                    msg.tasks &&
                                    msg.tasks.length
                                "
                                class="p-4 mt-3 bg-blue-50 border border-blue-200 rounded-lg"
                            >
                                <h3
                                    class="mb-3 text-sm font-bold text-blue-800"
                                >
                                    {{
                                        _mft(
                                            'dashboard:flowie.projectProposal.title'
                                        )
                                    }}
                                </h3>
                                <div class="space-y-1.5">
                                    <div
                                        v-for="(task, taskIndex) in msg.tasks"
                                        :key="taskIndex"
                                        class="flex items-center gap-2"
                                    >
                                        <template
                                            v-if="
                                                store.activeConversation
                                                    ?.type === 'project'
                                            "
                                        >
                                            <button-base
                                                @click="
                                                    store.toggleTask(
                                                        task.id ||
                                                            `task_${
                                                                taskIndex + 1
                                                            }`
                                                    )
                                                "
                                                :style-type="
                                                    tasks.find(
                                                        (t) =>
                                                            t.id ===
                                                            (task.id ||
                                                                `task_${
                                                                    taskIndex +
                                                                    1
                                                                }`)
                                                    )?.completed
                                                        ? 'primary'
                                                        : 'default'
                                                "
                                                size="xs"
                                                class="flex-shrink-0 !w-5 !h-5 !justify-center !px-0 !py-0"
                                            >
                                                <template
                                                    v-if="
                                                        tasks.find(
                                                            (t) =>
                                                                t.id ===
                                                                (task.id ||
                                                                    `task_${
                                                                        taskIndex +
                                                                        1
                                                                    }`)
                                                        )?.completed
                                                    "
                                                    #icon
                                                >
                                                    <svg
                                                        class="w-3 h-3 text-white"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        stroke-width="3"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    >
                                                        <polyline
                                                            points="20 6 9 17 4 12"
                                                        />
                                                    </svg>
                                                </template>
                                            </button-base>
                                        </template>
                                        <span
                                            v-else
                                            class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-400"
                                        ></span>
                                        <span
                                            class="flex-shrink-0 w-2.5 h-2.5 rounded-full"
                                            :class="
                                                specialistColors[
                                                    task.specialist
                                                ] || 'bg-gray-400'
                                            "
                                        ></span>
                                        <span class="text-sm text-gray-700">{{
                                            task.title
                                        }}</span>
                                    </div>
                                </div>
                                <div class="mt-3 pt-3 border-t border-blue-200">
                                    <template
                                        v-if="
                                            store.activeConversation?.type ===
                                            'project'
                                        "
                                    >
                                        <span
                                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-full"
                                        >
                                            <svg
                                                class="w-3.5 h-3.5"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            >
                                                <polyline
                                                    points="20 6 9 17 4 12"
                                                />
                                            </svg>
                                            {{
                                                _mft(
                                                    'dashboard:flowie.projectProposal.accepted'
                                                )
                                            }}
                                        </span>
                                    </template>
                                    <button-primary
                                        v-else
                                        @click="store.promoteToProject()"
                                        size="md"
                                    >
                                        {{
                                            _mft(
                                                'dashboard:flowie.projectProposal.accept'
                                            )
                                        }}
                                    </button-primary>
                                </div>
                            </div>

                            <!-- Action plan tasks (inline) -->
                            <div
                                v-if="
                                    msg.type === 'action_plan' &&
                                    msg.tasks &&
                                    msg.tasks.length &&
                                    i === latestActionPlanIndex
                                "
                                class="p-4 mt-3 bg-white border border-gray-200 rounded-lg"
                            >
                                <h3
                                    class="mb-3 text-sm font-bold text-gray-800"
                                >
                                    {{
                                        _mft(
                                            'dashboard:flowie.actionPlan.title'
                                        )
                                    }}
                                </h3>

                                <div class="space-y-2">
                                    <div
                                        v-for="(task, taskIndex) in msg.tasks"
                                        :key="task.id || taskIndex"
                                        class="flex items-center justify-between gap-3 py-1.5"
                                    >
                                        <div
                                            class="flex items-center gap-2.5 min-w-0"
                                        >
                                            <button-base
                                                @click="
                                                    store.toggleTask(
                                                        task.id ||
                                                            `task_${
                                                                taskIndex + 1
                                                            }`
                                                    )
                                                "
                                                :style-type="
                                                    tasks.find(
                                                        (t) =>
                                                            t.id ===
                                                            (task.id ||
                                                                `task_${
                                                                    taskIndex +
                                                                    1
                                                                }`)
                                                    )?.completed
                                                        ? 'primary'
                                                        : 'default'
                                                "
                                                size="xs"
                                                class="flex-shrink-0 !w-5 !h-5 !justify-center !px-0 !py-0"
                                            >
                                                <template
                                                    v-if="
                                                        tasks.find(
                                                            (t) =>
                                                                t.id ===
                                                                (task.id ||
                                                                    `task_${
                                                                        taskIndex +
                                                                        1
                                                                    }`)
                                                        )?.completed
                                                    "
                                                    #icon
                                                >
                                                    <svg
                                                        class="w-3 h-3 text-white"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        stroke-width="3"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    >
                                                        <polyline
                                                            points="20 6 9 17 4 12"
                                                        />
                                                    </svg>
                                                </template>
                                            </button-base>
                                            <span
                                                class="flex-shrink-0 w-2.5 h-2.5 rounded-full"
                                                :class="[
                                                    specialistColors[
                                                        task.specialist
                                                    ] || 'bg-gray-400',
                                                    {
                                                        'opacity-40':
                                                            tasks.find(
                                                                (t) =>
                                                                    t.id ===
                                                                    (task.id ||
                                                                        `task_${
                                                                            taskIndex +
                                                                            1
                                                                        }`)
                                                            )?.completed,
                                                    },
                                                ]"
                                            ></span>
                                            <span
                                                class="text-sm text-gray-700 truncate"
                                                :class="{
                                                    'line-through text-gray-400':
                                                        tasks.find(
                                                            (t) =>
                                                                t.id ===
                                                                (task.id ||
                                                                    `task_${
                                                                        taskIndex +
                                                                        1
                                                                    }`)
                                                        )?.completed,
                                                }"
                                            >
                                                {{ task.title }}
                                            </span>
                                        </div>
                                        <div class="flex-shrink-0">
                                            <a
                                                v-if="
                                                    getTaskLink(task) &&
                                                    isSpecialistHired(task) &&
                                                    !tasks.find(
                                                        (t) =>
                                                            t.id ===
                                                            (task.id ||
                                                                `task_${
                                                                    taskIndex +
                                                                    1
                                                                }`)
                                                    )?.completed
                                                "
                                                :href="getTaskLink(task)"
                                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors rounded-full text-wlPrimary bg-wlPrimary/10 hover:bg-wlPrimary/20"
                                            >
                                                {{
                                                    _mft(
                                                        'dashboard:flowie.actionPlan.start'
                                                    )
                                                }}
                                                <svg
                                                    class="w-3 h-3"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                >
                                                    <path d="M5 12h14" />
                                                    <path d="m12 5 7 7-7 7" />
                                                </svg>
                                            </a>
                                            <button
                                                v-else-if="
                                                    getTaskLink(task) &&
                                                    !isSpecialistHired(task) &&
                                                    !tasks.find(
                                                        (t) =>
                                                            t.id ===
                                                            (task.id ||
                                                                `task_${
                                                                    taskIndex +
                                                                    1
                                                                }`)
                                                    )?.completed
                                                "
                                                type="button"
                                                disabled
                                                :title="
                                                    _mft(
                                                        'dashboard:flowie.actionPlan.teammateNotHired'
                                                    )
                                                "
                                                :aria-label="
                                                    _mft(
                                                        'dashboard:flowie.actionPlan.teammateNotHired'
                                                    )
                                                "
                                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-400 bg-gray-100 rounded-full cursor-not-allowed"
                                            >
                                                {{
                                                    _mft(
                                                        'dashboard:flowie.actionPlan.start'
                                                    )
                                                }}
                                                <svg
                                                    class="w-3 h-3"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                >
                                                    <path d="M5 12h14" />
                                                    <path d="m12 5 7 7-7 7" />
                                                </svg>
                                            </button>
                                            <span
                                                v-else-if="
                                                    !tasks.find(
                                                        (t) =>
                                                            t.id ===
                                                            (task.id ||
                                                                `task_${
                                                                    taskIndex +
                                                                    1
                                                                }`)
                                                    )?.completed
                                                "
                                                class="inline-block px-2.5 py-1 text-xs font-medium text-gray-400 bg-gray-100 rounded-full"
                                            >
                                                {{
                                                    _mft(
                                                        'dashboard:aiSpecialists.comingSoon'
                                                    )
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Progress bar -->
                                <div
                                    v-if="tasks.length"
                                    class="pt-3 mt-3 border-t border-gray-100"
                                >
                                    <div
                                        class="flex items-center justify-between mb-1.5"
                                    >
                                        <span class="text-xs text-gray-500">
                                            {{
                                                store.activeConversation
                                                    ?.progress?.completed || 0
                                            }}
                                            {{
                                                _mft(
                                                    'dashboard:flowie.actionPlan.progress'
                                                )
                                            }}
                                            {{
                                                store.activeConversation
                                                    ?.progress?.total || 0
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        class="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden"
                                    >
                                        <div
                                            class="h-full transition-all duration-500 rounded-full bg-wlPrimary"
                                            :style="{
                                                width:
                                                    (store.activeConversation
                                                        ?.progress?.total
                                                        ? (store
                                                              .activeConversation
                                                              .progress
                                                              .completed /
                                                              store
                                                                  .activeConversation
                                                                  .progress
                                                                  .total) *
                                                          100
                                                        : 0) + '%',
                                            }"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Generating animation -->
                <div
                    v-if="store.isGenerating"
                    class="flex justify-start mb-3 generating-appear"
                >
                    <div
                        class="w-full max-w-[90%] rounded-2xl rounded-bl-md overflow-hidden shadow-lg border border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50"
                    >
                        <div class="h-1 w-full bg-gray-200 overflow-hidden">
                            <div
                                class="h-full bg-gradient-to-r from-wlPrimary via-wlPrimary/70 to-wlPrimary transition-all duration-700 ease-out"
                                :style="{ width: progressWidth + '%' }"
                            ></div>
                        </div>
                        <div class="px-5 py-4">
                            <div class="flex items-center gap-3 mb-3">
                                <div
                                    class="generating-icon flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-wlPrimary to-wlPrimary/70 flex items-center justify-center"
                                    :key="funMessageIndex"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="h-4 w-4 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="1.5"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            :d="currentFunMessage.icon"
                                        />
                                    </svg>
                                </div>
                                <span
                                    class="text-sm font-medium text-gray-700 generating-text"
                                    :key="'t' + funMessageIndex"
                                    >{{ currentFunMessage.text() }}</span
                                >
                            </div>
                            <div class="space-y-2">
                                <div class="flex gap-2">
                                    <div
                                        class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer"
                                        style="width: 65%"
                                    ></div>
                                    <div
                                        class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer"
                                        style="
                                            width: 25%;
                                            animation-delay: 0.3s;
                                        "
                                    ></div>
                                </div>
                                <div class="flex gap-2">
                                    <div
                                        class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer"
                                        style="
                                            width: 40%;
                                            animation-delay: 0.15s;
                                        "
                                    ></div>
                                    <div
                                        class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer"
                                        style="
                                            width: 50%;
                                            animation-delay: 0.45s;
                                        "
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Input -->
        <div class="relative p-4 border-t border-gray-100">
            <!-- Recording / Transcribing overlay -->
            <transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="scale-95 opacity-0"
                enter-to-class="scale-100 opacity-100"
                leave-active-class="transition duration-150 ease-in"
                leave-from-class="scale-100 opacity-100"
                leave-to-class="scale-95 opacity-0"
            >
                <div
                    v-if="isRecording || isTranscribing"
                    class="absolute inset-0 z-10 flex items-center justify-center gap-4 bg-white"
                >
                    <template v-if="isRecording">
                        <div class="flex items-center gap-3">
                            <div
                                class="relative flex items-center justify-center w-10 h-10"
                            >
                                <span
                                    class="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-red-400/50"
                                ></span>
                                <span
                                    class="relative flex items-center justify-center w-8 h-8 bg-red-500 rounded-full"
                                >
                                    <svg
                                        class="w-4 h-4 text-white"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <path
                                            d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
                                        />
                                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                    </svg>
                                </span>
                            </div>
                            <div class="flex gap-[3px] items-end h-5">
                                <span
                                    class="voice-bar w-[3px] rounded-full bg-red-400"
                                    style="animation-delay: 0ms"
                                ></span>
                                <span
                                    class="voice-bar w-[3px] rounded-full bg-red-400"
                                    style="animation-delay: 120ms"
                                ></span>
                                <span
                                    class="voice-bar w-[3px] rounded-full bg-red-400"
                                    style="animation-delay: 240ms"
                                ></span>
                                <span
                                    class="voice-bar w-[3px] rounded-full bg-red-400"
                                    style="animation-delay: 360ms"
                                ></span>
                                <span
                                    class="voice-bar w-[3px] rounded-full bg-red-400"
                                    style="animation-delay: 480ms"
                                ></span>
                            </div>
                            <span class="text-sm font-medium text-red-500">{{
                                _mft('dashboard:flowie.recording')
                            }}</span>
                        </div>
                        <button-secondary @click="stopRecording" size="md">
                            {{ _mft('dashboard:flowie.stopRecording') }}
                        </button-secondary>
                    </template>

                    <template v-else>
                        <div class="flex items-center gap-3">
                            <div
                                class="relative flex items-center justify-center w-10 h-10"
                            >
                                <svg
                                    class="w-6 h-6 animate-spin text-wlPrimary"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <circle
                                        class="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="3"
                                    />
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                            </div>
                            <span class="text-sm font-medium text-gray-500">{{
                                _mft('dashboard:flowie.transcribing')
                            }}</span>
                        </div>
                    </template>
                </div>
            </transition>

            <!-- Normal input row -->
            <div class="flex items-end gap-2">
                <textarea
                    ref="chatInputField"
                    v-model="chatInput"
                    rows="1"
                    class="flowie-chat-input flex-1 px-4 py-2 text-sm leading-5 border border-gray-200 rounded-lg resize-none overflow-y-auto focus:outline-none focus:ring-2 focus:ring-wlPrimary/30 focus:border-wlPrimary"
                    :placeholder="inputPlaceholder"
                    :disabled="store.isGenerating"
                    @keydown="handleKeydown"
                    @input="resizeChatInput"
                ></textarea>
                <button-base
                    @click="startRecording"
                    style-type="default"
                    :disabled="
                        store.isGenerating || isRecording || isTranscribing
                    "
                    size="sm"
                    class="!border-0 !bg-transparent !text-gray-500"
                >
                    <template #icon>
                        <svg
                            class="w-4 h-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path
                                d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
                            />
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                            <line x1="12" y1="19" x2="12" y2="23" />
                            <line x1="8" y1="23" x2="16" y2="23" />
                        </svg>
                    </template>
                </button-base>
                <button-base
                    @click="sendMessage()"
                    style-type="primary"
                    :disabled="store.isGenerating"
                    size="sm"
                    class="!px-4"
                >
                    <template #icon>
                        <svg
                            class="w-4 h-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                    </template>
                </button-base>
            </div>
        </div>
    </card-basic>
</template>

<style scoped>
.flowie-chat-input {
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.flowie-chat-input::-webkit-scrollbar {
    display: none;
}

.flowie-starter-avatar {
    background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.92),
            rgba(248, 250, 252, 0.78)
        ),
        radial-gradient(
            circle at 50% 0%,
            rgba(99, 102, 241, 0.16),
            transparent 68%
        );
    box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08),
        inset 0 0 0 1px rgba(148, 163, 184, 0.2);
}

.flowie-starter-bubble {
    position: relative;
    border-radius: 18px 18px 18px 8px;
    backdrop-filter: blur(10px);
}

.voice-bar {
    animation: voice-bar 0.8s ease-in-out infinite alternate;
}

@keyframes voice-bar {
    0% {
        height: 4px;
    }
    100% {
        height: 20px;
    }
}

@keyframes skeleton-shimmer {
    0%,
    100% {
        opacity: 0.4;
    }
    50% {
        opacity: 1;
    }
}
.skeleton-shimmer {
    animation: skeleton-shimmer 2s ease-in-out infinite;
}

@keyframes generating-appear {
    from {
        opacity: 0;
        transform: translateY(8px) scale(0.97);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}
.generating-appear {
    animation: generating-appear 0.4s ease-out;
}

@keyframes icon-pop {
    0% {
        transform: scale(0) rotate(-20deg);
    }
    60% {
        transform: scale(1.15) rotate(5deg);
    }
    100% {
        transform: scale(1) rotate(0deg);
    }
}
.generating-icon {
    animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes text-slide {
    from {
        opacity: 0;
        transform: translateX(-8px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}
.generating-text {
    animation: text-slide 0.4s ease-out;
}
</style>
